import { useEffect, useState } from 'react';
import { Encounter, Patient, Practitioner } from '@medplum/fhirtypes';
import { MedplumClient } from '@medplum/core';
import { HL7 } from '@/consts/systems.consts';

interface UseEncounterDetailsResult {
  loading: boolean;
  patient: Patient | null;
  attendingDoctor: Practitioner | null;
  referringDoctor: Practitioner | null;
  consultants: Practitioner[];
  additionalNotes: string | null;
  encounterStatus: string | null;
}

export function useEncounterDetails(
  encounter: Encounter | null,
  medplum: MedplumClient
): UseEncounterDetailsResult {
  const [loading, setLoading] = useState(false);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [attendingDoctor, setAttendingDoctor] = useState<Practitioner | null>(
    null
  );
  const [referringDoctor, setReferringDoctor] = useState<Practitioner | null>(
    null
  );
  const [consultants, setConsultants] = useState<Practitioner[]>([]);
  const [additionalNotes, setAdditionalNotes] = useState<string | null>(null);
  const [encounterStatus, setEncounterStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!encounter) {
      setPatient(null);
      setAttendingDoctor(null);
      setReferringDoctor(null);
      setConsultants([]);
      setAdditionalNotes(null);
      setEncounterStatus(null);
      return;
    }

    const fetchEncounterDetails = async () => {
      setLoading(true);
      try {
        const patientRef = encounter.subject?.reference;
        if (patientRef) {
          const patientResult = await medplum.readReference({
            reference: patientRef,
          } as any);
          setPatient(patientResult as Patient);
        } else {
          setPatient(null);
        }

        const participants = encounter.participant || [];

        const practitionerData = participants
          .filter((p) => p.individual?.reference?.startsWith('Practitioner/'))
          .map((p) => ({
            reference: p.individual?.reference,
            type: p.type?.[0]?.coding?.[0]?.code,
          }));

        const practitionerPromises = practitionerData.map(({ reference }) => {
          if (!reference) {
            return Promise.resolve(null);
          }
          return medplum.readReference({ reference } as any);
        });

        const allPractitioners = await Promise.all(practitionerPromises);

        let attending: Practitioner | null = null;
        let referring: Practitioner | null = null;
        const consultantList: Practitioner[] = [];

        allPractitioners.forEach((practitioner, index) => {
          if (!practitioner) {
            return;
          }

          const typeCode = practitionerData[index].type;

          if (
            typeCode === HL7.TERMINOLOGY.CS.V3.PARTICIPATION_TYPE.CODES.ATND
          ) {
            attending = practitioner as Practitioner;
          } else if (
            typeCode === HL7.TERMINOLOGY.CS.V3.PARTICIPATION_TYPE.CODES.REFERRER
          ) {
            referring = practitioner as Practitioner;
          } else if (
            typeCode === HL7.TERMINOLOGY.CS.V3.PARTICIPATION_TYPE.CODES.CON
          ) {
            consultantList.push(practitioner as Practitioner);
          }
        });

        setAttendingDoctor(attending);
        setReferringDoctor(referring);
        setConsultants(consultantList);

        setEncounterStatus(encounter.status || null);

        setAdditionalNotes(null);
      } catch (error) {
        console.error('Error fetching encounter details:', error);
        setPatient(null);
        setAttendingDoctor(null);
        setReferringDoctor(null);
        setConsultants([]);
        setAdditionalNotes(null);
        setEncounterStatus(null);
      } finally {
        setLoading(false);
      }
    };

    fetchEncounterDetails();
  }, [encounter, medplum]);

  return {
    loading,
    patient,
    attendingDoctor,
    referringDoctor,
    consultants,
    additionalNotes,
    encounterStatus,
  };
}
